// /client/src/components/Deployments.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Deployments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Checkbox selection state
  const [selectedDeployments, setSelectedDeployments] = useState([]);
  
  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  // Sync all messages with Twilio
  const syncAllMessages = async () => {
    try {
      setSyncing(true);
      setSyncMessage('');
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/deployments/sync-all', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSyncMessage(`${data.message}`);
        // Refresh deployments list after sync
        fetchDeployments();
      } else {
        setSyncMessage(`${data.error || 'Sync failed'}`);
      }
    } catch (err) {
      setSyncMessage(`✗ ${err.message}`);
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  // Fetch deployments from API
  const fetchDeployments = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      const response = await fetch('/api/deployments', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeployments(data.deployments || []);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(errorData.error || 'Failed to fetch deployments');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load deployments');
    } finally {
      setLoading(false);
    }
  };

  // Sync messages and fetch deployments on component mount
  useEffect(() => {
    const initializeData = async () => {
      await syncAllMessages();
    };
    initializeData();
  }, []);

  // Check for success message from location state
  useEffect(() => {
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the location state
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort deployments
  const filteredAndSortedDeployments = useMemo(() => {
    let filtered = [...deployments];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(deployment => {
        const name = deployment.name?.toLowerCase() || '';
        const programName = deployment.programId?.programName?.toLowerCase() || '';
        const productName = deployment.productId?.name?.toLowerCase() || '';
        
        return name.includes(searchLower) || 
               programName.includes(searchLower) || 
               productName.includes(searchLower);
      });
    }
    
    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(deployment => deployment.status === selectedStatus);
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'programName') {
          aValue = a.programId?.programName || '';
          bValue = b.programId?.programName || '';
        } else if (sortConfig.key === 'productName') {
          aValue = a.productId?.name || '';
          bValue = b.productId?.name || '';
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }
        
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'scheduledStartDate' || sortConfig.key === 'scheduledEndDate') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [deployments, searchTerm, selectedStatus, sortConfig]);

  // Check if deployment can be cancelled
  const canBeCancelled = (deployment) => {
    return ['draft', 'scheduled', 'in delivery'].includes(deployment.status);
  };

  // Get all cancellable deployments in filtered list
  const cancellableDeployments = useMemo(() => {
    return filteredAndSortedDeployments.filter(canBeCancelled);
  }, [filteredAndSortedDeployments]);

  // Checkbox handlers
  const handleSelectDeployment = (id) => {
    setSelectedDeployments(prev => {
      if (prev.includes(id)) {
        return prev.filter(deploymentId => deploymentId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    // Only select cancellable deployments
    const cancellableIds = cancellableDeployments.map(d => d._id);
    if (selectedDeployments.length === cancellableIds.length && cancellableIds.length > 0) {
      setSelectedDeployments([]);
    } else {
      setSelectedDeployments(cancellableIds);
    }
  };

  // Cancel handlers
  const handleCancelClick = () => {
    if (selectedDeployments.length === 0) {
      setError('No deployments selected.');
      return;
    }
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleBulkCancel = async () => {
    if (selectedDeployments.length === 0) {
      setError('No deployments selected');
      return;
    }

    try {
      setCancelling(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/deployments/bulk/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deploymentIds: selectedDeployments,
          reason: cancelReason.trim() || 'Bulk cancelled by admin'
        })
      });

      if (response.ok) {
        const data = await response.json();
        let message = data.message || `Successfully cancelled ${data.cancelledCount} deployment${data.cancelledCount !== 1 ? 's' : ''}`;
        if (data.totalMessagesCancelled > 0) {
          message += ` - ${data.totalMessagesCancelled} queued message${data.totalMessagesCancelled !== 1 ? 's' : ''} removed`;
        }
        setSuccessMessage(message);
        setShowCancelModal(false);
        setSelectedDeployments([]);
        setCancelReason('');
        // Refresh deployments list
        fetchDeployments();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to cancel deployments');
      }
    } catch (error) {
      console.error('Error cancelling deployments:', error);
      setError('Failed to cancel deployments');
    } finally {
      setCancelling(false);
    }
  };

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const handleRowClick = (id, e) => {
    if (e.target.type === 'checkbox') {
      return;
    }
    navigate(`/deployments/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleCreateDeployment = () => {
    navigate('/deployments/create');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'is-light';
      case 'scheduled':
        return 'is-info';
      case 'in delivery':
        return 'is-warning';
      case 'completed':
        return 'is-success';
      case 'cancelled':
        return 'is-danger';
      default:
        return 'is-light';
    }
  };

  const getStatusLabel = (status) => {
    if (status === 'in delivery') return 'In Delivery';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
  };

  if (loading) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading deployments...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Deployments</h1>
              <div className="is-flex" style={{ gap: '10px' }}>
                {selectedDeployments.length > 0 && (
                  <button 
                    className="button is-danger is-outlined"
                    onClick={handleCancelClick}
                  >
                    Cancel {selectedDeployments.length} Deployment{selectedDeployments.length !== 1 ? 's' : ''}
                  </button>
                )}
                <button 
                  className="button is-primary"
                  onClick={handleCreateDeployment}
                >
                  Create New Deployment
                </button>
              </div>
            </div>

            {/* Sync Message */}
            {syncMessage && (
              <div className={`notification ${syncMessage.startsWith('✗') ? 'is-danger' : 'is-success'} is-light py-2 mb-4`}>
                <button 
                  className="delete" 
                  onClick={() => setSyncMessage('')}
                ></button>
                {syncMessage}
              </div>
            )}

            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchDeployments}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Search and Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="pt-0 label">Search</label>
                    <div className="control has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="Search by name, program, or product..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="icon is-small is-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="column is-4">
                  <div className="field">
                    <label className="pt-0 label">Filter by Status</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="draft">Draft</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="in delivery">In Delivery</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!searchTerm && !selectedStatus}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Selection count display */}
            {selectedDeployments.length > 0 && (
              <div className="notification is-info is-light mb-4">
                <p>
                  <span className='colorBlack'>{selectedDeployments.length}</span> deployment{selectedDeployments.length !== 1 ? 's' : ''} selected for cancellation.
                </p>
              </div>
            )}

            {filteredAndSortedDeployments.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {searchTerm || selectedStatus
                    ? 'No deployments match your search criteria.'
                    : 'No deployments found. Create your first deployment to get started.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="is-size-7">
                    Showing {filteredAndSortedDeployments.length} of {deployments.length} deployment{deployments.length !== 1 ? 's' : ''}
                    {cancellableDeployments.length > 0 && (
                      <>
                        {' • '}
                        <span className="has-text-info has-text-weight-semibold">
                          {cancellableDeployments.length} selectable
                        </span>
                      </>
                    )}
                  </p>
                  {(searchTerm || selectedStatus) && (
                    <div className="tags">
                      {searchTerm && (
                        <span className="tag is-info">
                          Search: "{searchTerm}"
                          <button 
                            className="delete is-small" 
                            onClick={() => setSearchTerm('')}
                          ></button>
                        </span>
                      )}
                      {selectedStatus && (
                        <span className="tag is-info">
                          Status: {getStatusLabel(selectedStatus)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setSelectedStatus('')}
                          ></button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedDeployments.length === cancellableDeployments.length && cancellableDeployments.length > 0}
                            onChange={handleSelectAll}
                            disabled={cancellableDeployments.length === 0}
                            title={cancellableDeployments.length === 0 ? 'No cancellable deployments' : `Select all ${cancellableDeployments.length} cancellable`}
                          />
                        </th>
                        <th 
                          onClick={() => handleSort('name')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'name' ? 'colorBlack' : 'colorBlue'}
                        >
                          Name {getSortIcon('name')}
                        </th>
                        <th 
                          onClick={() => handleSort('programName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'programName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Program {getSortIcon('programName')}
                        </th>
                        <th 
                          onClick={() => handleSort('productName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'productName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Product {getSortIcon('productName')}
                        </th>
                        <th 
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                        >
                          Status {getSortIcon('status')}
                        </th>
                        <th>Pods</th>
                        <th 
                          onClick={() => handleSort('scheduledStartDate')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'scheduledStartDate' ? 'colorBlack' : 'colorBlue'}
                        >
                          Start {getSortIcon('scheduledStartDate')}
                        </th>
                        {/* <th 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                        >
                          Created {getSortIcon('createdAt')}
                        </th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedDeployments.map((deployment) => {
                        const id = deployment._id;
                        const isSelected = selectedDeployments.includes(id);
                        const isCancellable = canBeCancelled(deployment);
                        
                        return (
                          <tr 
                            key={id}
                            onClick={(e) => handleRowClick(id, e)}
                            style={{
                              cursor: 'pointer',
                              transition: 'transform 0.1s ease',
                              backgroundColor: isSelected ? '#f0f8ff' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#fafafa';
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              {isCancellable ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectDeployment(id)}
                                />
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={false}
                                  disabled
                                  title="Cannot cancel - already completed or cancelled"
                                  style={{ opacity: 0.3, cursor: 'not-allowed' }}
                                />
                              )}
                            </td>
                            <td>
                              <span className='colorBlack'>{deployment.name}</span>
                            </td>
                            <td>{deployment.programId?.programName || '-'}</td>
                            <td>
                              {deployment.productId?.name || '-'}
                              {deployment.productId?.days && (
                                <span className="has-text-grey is-size-7 ml-1">
                                  ({deployment.productId.days} msgs)
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`tag ${getStatusColor(deployment.status)}`}>
                                {getStatusLabel(deployment.status)}
                              </span>
                            </td>
                            <td>
                              {deployment.pods?.length || 0}
                              {deployment.metadata?.totalContacts > 0 && (
                                <span className="has-text-grey is-size-7 ml-1">
                                  ({deployment.metadata.totalContacts} contacts)
                                </span>
                              )}
                            </td>
                            <td>{formatDateTime(deployment.scheduledStartDate)}</td>
                            {/* <td>{formatDate(deployment.createdAt)}</td> */}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>

        </div>
      </section>

      {/* Cancel Deployments Modal */}
      {showCancelModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowCancelModal(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head has-background-danger-light">
              <h2 className="modal-card-title is-bold">
                Cancel Deployment{selectedDeployments.length !== 1 ? 's' : ''}
              </h2>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowCancelModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              <div className="content">
                <p>Are you sure you want to cancel the following deployment{selectedDeployments.length !== 1 ? 's' : ''}?</p>
                
                <div className="box has-background-light" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <ul className="mt-0">
                    {selectedDeployments.map(id => {
                      const deployment = filteredAndSortedDeployments.find(d => d._id === id);
                      return (
                        <li key={id}>
                          <span className='colorBlack'>{deployment?.name}</span>
                          <span className={`ml-2 tag is-small ${getStatusColor(deployment?.status)}`}>
                            {getStatusLabel(deployment?.status)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="field mt-4">
                  <label className="label">Cancellation Reason (Optional)</label>
                  <div className="control">
                    <textarea
                      className="textarea"
                      placeholder="Enter reason for cancellation..."
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="notification is-danger is-light mt-4">
                  <span className='colorBlack'>Warning:</span> This action cannot be undone. 
                  <ul className="mt-2 mb-0">
                    <li>All pending messages in the queue will be removed</li>
                    <li>Messages already sent to Twilio may still be delivered</li>
                    <li>The deployment{selectedDeployments.length !== 1 ? 's' : ''} will be marked as cancelled</li>
                  </ul>
                </div>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`button is-danger mx-1 ${cancelling ? 'is-loading' : ''}`}
                onClick={handleBulkCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : `Yes, Cancel ${selectedDeployments.length} Deployment${selectedDeployments.length !== 1 ? 's' : ''}`}
              </button>
              <button 
                className="button mx-1" 
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                No, Keep Deployment{selectedDeployments.length !== 1 ? 's' : ''}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default Deployments;